import type { QuoteItem } from '@elka/types';

/**
 * Calculate quote item sum
 */
export function calculateQuoteItemSum(item: QuoteItem): number {
  if (item.isHeader) return 0;
  const subtotal = item.quantity * item.unitPrice;
  const discountAmount = item.discount ? subtotal * (item.discount / 100) : 0;
  return subtotal - discountAmount;
}

/**
 * Calculate quote subtotal
 */
export function calculateQuoteSubtotal(items: QuoteItem[]): number {
  return items.reduce((sum, item) => sum + calculateQuoteItemSum(item), 0);
}

/**
 * Calculate quote total with tax
 */
export function calculateQuoteTotal(
  subtotal: number,
  taxRate: number
): {
  taxAmount: number;
  total: number;
} {
  const taxAmount = subtotal * taxRate;
  const total = subtotal + taxAmount;
  return { taxAmount, total };
}

/**
 * Calculate labor cost
 */
export function calculateLaborCost(laborDays: number, dayRate: number): number {
  return laborDays * dayRate;
}

/**
 * Calculate final client price with markup over cost
 * Formula: totalCost × (1 + marginPercent/100)
 * e.g., with 30% markup: cost × 1.3
 */
export function calculateFinalPrice(totalCost: number, marginPercent: number): number {
  return totalCost * (1 + marginPercent / 100);
}

/**
 * Snap float noise before comparing a computed ratio/percentage/amount against a
 * fixed threshold. Chained float subtraction/division can turn an exact 30% into
 * 29.999999999999993 — round to `decimals` places so a real boundary hit compares
 * equal, without losing precision any of these comparisons actually care about.
 * Use ~2 decimals for money (cents), ~6 for percentages/ratios (default).
 */
export function roundForCompare(n: number, decimals = 6): number {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

export interface DiscountStep {
  type: 'percent' | 'fixed';
  value: number;
  /** Discount in ₪ taken by this step */
  amount: number;
}

/**
 * Apply stacked discounts: discount #1 (discountType/discountValue) then each extra one,
 * every step taken from the remainder left by the previous steps. Zero-value steps are skipped.
 * `total` is the combined discount in ₪.
 */
export function applyDiscounts(
  itemsSubtotal: number,
  d: {
    discountType?: 'percent' | 'fixed';
    discountValue?: number;
    extraDiscounts?: { type: 'percent' | 'fixed'; value: number }[];
  }
): { steps: DiscountStep[]; total: number } {
  const all = [
    { type: d.discountType ?? 'percent', value: d.discountValue ?? 0 },
    ...(d.extraDiscounts ?? []),
  ];
  const steps: DiscountStep[] = [];
  let remaining = itemsSubtotal;
  let total = 0;
  for (const { type, value } of all) {
    if (!value) continue;
    const amount = type === 'percent' ? (remaining * value) / 100 : Math.min(value, remaining);
    steps.push({ type, value, amount });
    remaining -= amount;
    total += amount;
  }
  return { steps, total };
}
